import * as React from 'react';
import { useIntl } from 'react-intl';
import { activityLabel, activityVerb, finishedSteps, formatDuration, localeOf, type ActivityState } from './copilotActivity';

/**
 * Ligne d'activité éphémère du copilote (UPT-2041, lot 1) : montre le travail pendant qu'il se fait, puis
 * disparaît (le bilan durable est `CopilotSummaryLine`, sous la réponse).
 *
 *  - une ligne : indicateur animé, verbe courant traduit (« Je parcours « Dossiers BCM » (6)… »), temps écoulé
 *    à la seconde, bouton Stop (côté client : coupe la lecture du flux, le serveur termine seul) ;
 *  - dessous, les trois dernières étapes terminées en petit, la plus ancienne estompée (transition d'opacité,
 *    supprimée sous `prefers-reduced-motion` : il reste la liste) ;
 *  - aucune étape reçue (ancien serveur) : on retombe sur l'indicateur « Réflexion… » historique.
 *
 * Le verbe n'est pas une région vocale ici : la région `role="status"` est montée par le tiroir, AVANT tout
 * contenu (une région insérée déjà remplie n'est pas toujours annoncée), et c'est elle qui reçoit le verbe. La
 * ligne visible est donc masquée aux lecteurs d'écran, comme le compteur de secondes (sinon le temps serait annoncé
 * chaque seconde) et la liste des étapes terminées ; le bouton Stop, lui, reste accessible.
 */

/** De la plus récente à la plus ancienne : opacité décroissante (classes littérales pour Tailwind). */
const STEP_OPACITY = ['opacity-100', 'opacity-60', 'opacity-30'] as const;

export interface CopilotActivityLineProps {
  activity: ActivityState;
  onStop: () => void;
}

export function CopilotActivityLine({ activity, onStop }: CopilotActivityLineProps) {
  const intl = useIntl();
  const t = React.useCallback(
    (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values),
    [intl],
  );

  // Compteur local à la seconde ; l'intervalle est nettoyé au démontage (la ligne n'existe que pendant `busy`).
  const [now, setNow] = React.useState<number>(() => Date.now());
  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const elapsedMs = Math.max(0, Math.floor((now - activity.startedAt) / 1000)) * 1000;

  const verb = activityVerb(activity, t);
  const steps = finishedSteps(activity.items);

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-xs text-portal-ink-muted">
        <div aria-hidden="true" className="flex min-w-0 flex-1 items-center gap-2 italic">
          <i className="fa fa-cog animate-spin motion-reduce:animate-none" aria-hidden="true" />
          <span className="min-w-0 truncate">{verb}</span>
        </div>
        <span aria-hidden="true" className="shrink-0 tabular-nums">
          {formatDuration(elapsedMs, localeOf(intl))}
        </span>
        <button
          type="button"
          onClick={onStop}
          aria-label={t('copilot.activity.stopAria')}
          className="inline-flex shrink-0 items-center gap-1 rounded border border-portal-border px-2 py-0.5 text-xs text-portal-ink hover:border-portal-accent pointer-coarse:min-h-11 pointer-coarse:px-3"
        >
          <i className="fa fa-stop" aria-hidden="true" />
          <span>{t('copilot.activity.stop')}</span>
        </button>
      </div>
      {steps.length > 0 ? (
        <ul aria-hidden="true" className="space-y-0.5 ps-5 text-xs text-portal-ink-muted">
          {steps.map((step, index) => (
            <li
              key={step.seq}
              className={`truncate transition-opacity duration-500 motion-reduce:transition-none ${STEP_OPACITY[steps.length - 1 - index]}`}
            >
              {activityLabel(step, t)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default CopilotActivityLine;
